<template>
  <div>
    <b-container fluid>
      <h3 class="mt-3">
        {{ $t('order-intake-form.title') }}
      </h3>
      <b-card bg-variant="light" class="my-3">
        <b-row>
          <b-col md="4">
            <b-form-group :label="$t('forms.filter.search') + ':'">
              <b-input-group>
                <b-form-input
                  v-model="filterSearch"
                  :placeholder="$t('forms.filter.search-placeholder')"
                />
                <b-input-group-append v-if="filterSearch">
                  <b-btn @click="filterSearch = ''">&times;</b-btn>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <div class="overflow-auto">
        <b-table
          show-empty
          striped
          stacked="md"
          :items="filteredForms"
          :busy="isLoading"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDir"
          :per-page="perPage"
          :current-page="currentPage"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>Loading...</strong>
            </div>
          </template>
          <template #cell(name)="{ item }">
            <a href="#" @click.prevent="navigateToForm(item)" class="form-link">
              {{ item.name }}
            </a>
          </template>
        </b-table>
      </div>

      <b-pagination
        v-if="totalFilteredRows > perPage"
        :total-rows="totalFilteredRows"
        :per-page="perPage"
        v-model="currentPage"
        align="center"
      />
      <div v-if="totalFilteredRows" class="text-center text-muted">
        <small>{{ paginatedForms.length }} / {{ totalFilteredRows }}</small>
      </div>
    </b-container>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

export default {
  name: 'OrderCreationForms',

  data() {
    return {
      allForms: [],
      isLoading: true,
      filterSearch: '',
      currentPage: 1,
      perPage: 10,
      sortBy: null,
      sortDir: false,
      fields: [
        {
          key: 'name',
          label: this.$t('forms.table.name'),
          sortable: true,
        },
        {
          key: 'opportunity_name',
          label: this.$t('forms.table.opportunity-name'),
          sortable: true,
        },
        {
          key: 'opportunity',
          label: this.$t('forms.table.opportunity'),
          sortable: true,
        },
      ],
    };
  },

  computed: {
    ...mapGetters(['user', 'locale']),

    // Filter forms based on search term
    filteredForms() {
      if (!this.filterSearch) {
        return this.allForms;
      }

      const searchTerm = this.filterSearch.toLowerCase();
      return this.allForms.filter(
        (form) =>
          form.name?.toLowerCase().includes(searchTerm) ||
          form.opportunity_name?.toLowerCase().includes(searchTerm) ||
          form.opportunity?.toLowerCase().includes(searchTerm),
      );
    },

    // Get total number of filtered rows
    totalFilteredRows() {
      return this.filteredForms.length;
    },

    // Sort and paginate the filtered forms
    paginatedForms() {
      let forms = [...this.filteredForms];

      // Sort if needed
      if (this.sortBy) {
        forms.sort((a, b) => {
          let aVal = a[this.sortBy];
          let bVal = b[this.sortBy];

          // Handle null values
          if (aVal === null) return 1;
          if (bVal === null) return -1;

          // Compare values
          if (typeof aVal === 'string') {
            aVal = aVal.toLowerCase();
            bVal = bVal.toLowerCase();
          }

          if (this.sortDir) {
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
          } else {
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          }
        });
      }

      // Calculate pagination slice
      const startIndex = (this.currentPage - 1) * this.perPage;
      const endIndex = startIndex + this.perPage;

      return forms.slice(startIndex, endIndex);
    },
  },

  watch: {
    filterSearch() {
      this.currentPage = 1;
    },
    user: function (newUser) {
      if (newUser?.account?.id) {
        this.fetchForms();
      }
    },
  },

  methods: {
    async fetchForms() {
      try {
        this.isLoading = true;
        const { data } = await axios.get(
          `/order-intake-forms-api/form/brand/${this.user.account.id}/startegy/creation`,
        );

        this.allForms = data.map((form) => ({
          id: form.form_id,
          name: form.form_name,
          opportunity: form.opportunity_id,
          opportunity_name: form.opportunity_name,
        }));

        this.isLoading = false;
      } catch (error) {
        console.error('Failed to fetch forms:', error);
        this.isLoading = false;
      }
    },

    navigateToForm(form) {
      const formData = encodeURIComponent(JSON.stringify(form));
      this.$router.push({
        name: 'order-intake-forms',
        params: {
          form_id: form.id,
        },
        query: {
          formData: formData,
        },
      });
    },
  },

  created() {
    this.fetchForms();
  },
};
</script>

<style lang="scss" scoped>
.table-responsive {
  min-height: 300px;
}
</style>
